<template>
  <div class="site">
    <header class="hero">
      <div class="hero__grid" aria-hidden="true"></div>
      <div class="hero__glow" aria-hidden="true"></div>

      <div class="hero__inner">
        <div class="hero__copy">
          <p class="hero__eyebrow reveal" style="--d: 0s">AI, Software Engineer</p>
          <h1 class="hero__name reveal" style="--d: 0.08s">Tobi Obadiah</h1>
          <p class="hero__lede reveal" style="--d: 0.16s">
            AI engineer working in C, C++, and Python, building systems for
            learning and retrieval, with research interest in long-term memory
            and conscious AI.
          </p>
          <p class="hero__email reveal" style="--d: 0.24s">
            tobi [dot] obadiah1 [at] gmail
          </p>

          <div class="hero__socials reveal" style="--d: 0.3s">
            <a
              class="social"
              target="_blank"
              rel="noopener"
              href="https://github.com/ytobi"
              aria-label="GitHub"
            >
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <path
                  fill="currentColor"
                  d="M12 2C6.48 2 2 6.58 2 12.26c0 4.52 2.87 8.35 6.84 9.7.5.1.68-.22.68-.48 0-.24-.01-.87-.01-1.7-2.78.62-3.37-1.37-3.37-1.37-.45-1.18-1.11-1.5-1.11-1.5-.91-.64.07-.63.07-.63 1 .07 1.53 1.06 1.53 1.06.89 1.56 2.34 1.11 2.91.85.09-.66.35-1.11.63-1.37-2.22-.26-4.55-1.14-4.55-5.07 0-1.12.39-2.03 1.03-2.75-.1-.26-.45-1.31.1-2.73 0 0 .84-.28 2.75 1.05A9.3 9.3 0 0 1 12 6.84c.85.004 1.71.12 2.51.35 1.9-1.33 2.74-1.05 2.74-1.05.55 1.42.2 2.47.1 2.73.64.72 1.03 1.63 1.03 2.75 0 3.94-2.34 4.8-4.57 5.06.36.32.68.94.68 1.9 0 1.37-.01 2.47-.01 2.81 0 .26.18.58.69.48A10.27 10.27 0 0 0 22 12.26C22 6.58 17.52 2 12 2z"
                />
              </svg>
            </a>
            <a
              class="social"
              target="_blank"
              rel="noopener"
              href="https://www.linkedin.com/in/tobi-obadiah/"
              aria-label="LinkedIn"
            >
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <path
                  fill="currentColor"
                  d="M4.98 3.5C4.98 4.88 3.86 6 2.5 6S0 4.88 0 3.5 1.12 1 2.5 1s2.48 1.12 2.48 2.5zM.22 8.25h4.56V23H.22V8.25zM8.34 8.25h4.37v2.01h.06c.61-1.15 2.1-2.36 4.32-2.36 4.62 0 5.47 3.04 5.47 7V23h-4.56v-6.59c0-1.57-.03-3.59-2.19-3.59-2.19 0-2.53 1.71-2.53 3.48V23H8.34V8.25z"
                />
              </svg>
            </a>
            <a
              class="social"
              target="_blank"
              rel="noopener"
              href="https://stackoverflow.com/users/7158019/t-obadiah"
              aria-label="Stack Overflow"
            >
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <path
                  fill="currentColor"
                  d="M18.99 20.94v-5.65h1.9V23H2.3v-7.71h1.9v5.65h14.79zM7.22 17.7h9.56v1.9H7.22v-1.9zm.23-4.33 9.33 1.96.4-1.86-9.33-1.96-.4 1.86zm1.23-4.52 8.63 4.03.8-1.72-8.63-4.03-.8 1.72zm2.4-4.3 7.32 6.1 1.2-1.44-7.32-6.1-1.2 1.44zm4.7-4.55-1.52 1.12 5.72 7.74 1.52-1.12-5.72-7.74z"
                />
              </svg>
            </a>
            <a
              class="social"
              href="mailto:tobi.obadiah1@gmail.com"
              aria-label="Email"
            >
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <path
                  fill="currentColor"
                  d="M20 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4-8 5L4 8V6l8 5 8-5v2z"
                />
              </svg>
            </a>
          </div>

          <nav class="hero__nav reveal" style="--d: 0.38s" aria-label="Page">
            <a href="#about">About</a>
            <a href="#experiences">Experience</a>
            <a href="#education">Education</a>
            <a href="#research">Research</a>
            <a href="#misc">Misc</a>
            <a href="Tobi Obadiah - Resume.pdf">Resume</a>
          </nav>
        </div>

        <div class="hero__media reveal" style="--d: 0.2s">
          <img
            class="hero__photo"
            alt="Portrait of Tobi Obadiah"
            src="../assets/20271043.webp"
          />
        </div>
      </div>
    </header>

    <main>
      <section class="section" id="about">
        <div class="section__inner">
          <h2 class="section__title">About</h2>
          <div class="prose">
            <p>
              AI engineer based in the United Kingdom, currently pursuing an
              MSc in Artificial Intelligence at
              <a target="_blank" rel="noopener" href="https://www.dmu.ac.uk/"
                >De Montfort University</a
              >. My work sits at the intersection of machine learning and
              high-performance systems in C, C++, and Python, with research
              interest in long-term memory for LLMs and conscious AI, the idea
              that intelligence follows consciousness.
            </p>
            <p>
              I care about clear systems thinking, reliable delivery, and
              explaining technical trade-offs to both engineers and
              non-specialists. Outside work I follow reinforcement learning and
              AI research, play chess and table tennis, and stay curious about
              outer space. I have also freelanced on
              <a
                target="_blank"
                rel="noopener"
                href="https://www.upwork.com/freelancers/oluwatobiobadiah"
                >Upwork</a
              >
              and
              <a
                target="_blank"
                rel="noopener"
                href="https://www.fiverr.com/tobiobadiah"
                >Fiverr</a
              >
              with consistently strong client feedback.
            </p>
          </div>
        </div>
      </section>

      <section class="section section--tint" id="experiences">
        <div class="section__inner">
          <h2 class="section__title">Experience</h2>
          <ul class="experience-timeline">
            <li
              v-for="(expr, item) in experiences"
              :key="item"
              class="experience-timeline__item"
            >
              <Experience
                :title="expr.title"
                :assignments="expr.assignments"
                :tech_stack="expr.tech_stack"
                :company="expr.company"
                :company-url="expr.companyUrl"
                :company-links="expr.companyLinks"
                :from="expr.from"
                :to="expr.to"
              />
            </li>
          </ul>
        </div>
      </section>

      <section class="section" id="education">
        <div class="section__inner">
          <h2 class="section__title">Education</h2>
          <ul class="timeline">
            <li
              v-for="(edu, item) in education"
              :key="item"
              class="timeline__item"
            >
              <Education
                :degree="edu.degree"
                :school="edu.school"
                :from="edu.from"
                :to="edu.to"
                :year="edu.year"
              />
            </li>
          </ul>
          <p class="more-msc">
            <span class="more-msc__label">Also:</span>
            <template v-for="(cert, i) in certificates" :key="cert">
              <span class="more-msc__plain">{{ cert }}</span>
              <span
                v-if="i < certificates.length - 1"
                class="more-msc__sep"
              >
                ·
              </span>
            </template>
          </p>
        </div>
      </section>

      <section class="section section--tint" id="research">
        <div class="section__inner">
          <h2 class="section__title">Featured research and projects</h2>
          <ul class="project-list">
            <li
              v-for="(prj, item) in featuredResearch"
              :key="'r-' + item"
              class="project-list__item"
            >
              <Projects
                featured
                :slug="prj.slug"
                :title="prj.title"
                :description="prj.description"
                :code="prj.code"
                :website="prj.website"
                :image="prj.image"
                :status="prj.status"
              />
            </li>
          </ul>
          <p v-if="moreMsc.length" class="more-msc">
            <span class="more-msc__label">Also from the MSc:</span>
            <template v-for="(prj, i) in moreMsc" :key="prj.slug">
              <router-link
                v-if="projectDetailPagesEnabled"
                class="more-msc__link"
                :to="{ name: 'Project', params: { slug: prj.slug } }"
              >
                {{ prj.title }}
              </router-link>
              <span v-else class="more-msc__plain">{{ prj.title }}</span>
              <span v-if="i < moreMsc.length - 1" class="more-msc__sep"> · </span>
            </template>
          </p>
        </div>
      </section>

      <section class="section" id="misc">
        <div class="section__inner">
          <h2 class="section__title">Misc</h2>
          <p class="section__lede">
            Outside the work. Walks, travel, and a bit of play.
          </p>
          <div class="misc-grid">
            <figure
              v-for="(item, i) in miscMedia"
              :key="i"
              class="misc-grid__item"
              :class="'misc-grid__item--' + item.orient"
            >
              <video
                v-if="item.type === 'video'"
                class="misc-grid__media"
                :src="item.src"
                :poster="item.poster"
                controls
                muted
                playsinline
                preload="metadata"
                loop
              />
              <img
                v-else
                class="misc-grid__media"
                :src="item.src"
                :alt="item.alt"
                loading="lazy"
              />
            </figure>
          </div>
        </div>
      </section>
    </main>

    <footer class="footer">
      <div class="footer__inner">
        <span>Copyright {{ year }} Tobi Obadiah</span>
      </div>
    </footer>
  </div>
</template>

<script>
import Experience from '@/components/Experience.vue'
import Education from '@/components/Education.vue'
import Projects from '@/components/Projects.vue'
import { featuredProjects, projectDetailPagesEnabled } from '@/data/featuredProjects'
import misc1 from '@/assets/misc/web/1.jpg'
import misc2 from '@/assets/misc/web/2.jpg'
import misc3 from '@/assets/misc/web/3.jpg'
import misc8 from '@/assets/misc/web/8.mp4'
import misc8Poster from '@/assets/misc/web/8-poster.jpg'

const HOME_FEATURED_SLUGS = [
  'graph-db',
  'graph-memory'
]

export default {
  name: 'Home',
  components: {
    Experience,
    Education,
    Projects
  },
  data () {
    return {
      year: new Date().getFullYear(),
      projectDetailPagesEnabled,
      miscMedia: [
        {
          type: 'image',
          src: misc1,
          alt: 'Looking out from a rocky overlook over a wooded valley',
          orient: 'tall'
        },
        {
          type: 'image',
          src: misc2,
          alt: 'Playful hike on a rocky hillside in Cameroon',
          orient: 'tall'
        },
        {
          type: 'image',
          src: misc3,
          alt: 'Hiking selfie on a rocky hillside',
          orient: 'tall'
        },
        {
          type: 'video',
          src: misc8,
          poster: misc8Poster,
          alt: 'Short outdoor clip',
          orient: 'tall'
        }
      ],
      experiences: [
        {
          title: 'Backend Engineer (contract)',
          company: 'Neptune Technologies LLC',
          from: '03/2024',
          to: '10/2024',
          assignments: [
            'Built FastAPI backend services for fintech CRM workflows, improving scalability and maintainability.',
            'Integrated Stripe subscriptions API into the CRM, enabling seamless automated billing and payment processing.',
            'Collaborated with cross-functional teams to streamline feature integration, reducing deployment time by 30%.'
          ]
        },
        {
          title: 'Full-stack Developer',
          company: 'N01ZET',
          from: '04/2022',
          to: '05/2024',
          assignments: [
            'Led a 5-engineer team, improving app performance by 60% with ReactJS, TypeScript, and Node.js.',
            'Created and deployed EOSIO blockchain smart contracts, enabling secure transactions for 10+ organizations.',
            'Implemented Kafka-based microservices, scaling to thousands of users and reducing response time by 30%.'
          ]
        },
        {
          title: 'Machine Learning Engineer',
          company: 'Mboa Digital',
          from: '11/2020',
          to: '06/2021',
          assignments: [
            'Developed ETL machine learning pipeline for automated bank statement and invoice reconciliation with 90% accuracy.',
            'Evaluated XGBoost, Random Forest, Linear Regression, SVMs, and neural networks for financial document matching.',
            'Integrated Google Document AI and deployed scalable inference pipelines on AWS Lambda and API Gateway.'
          ]
        },
        {
          title: 'Software and AI Engineer',
          company: 'Artificials & Engineering',
          from: '11/2019',
          to: '10/2020',
          assignments: [
            'Engineered core features for the Ride & Smile transport application using C++ and MySQL, optimizing performance.',
            'Optimized production deployment with comprehensive testing via Google’s GTest, boosting feature validation by 50%.',
            'Developed Python and PySide (Qt) app for data analysis and visualization, improving data interpretation and reporting.'
          ]
        },
        {
          title: 'Freelance Software Developer',
          company: 'Upwork & Fiverr',
          companyLinks: [
            {
              name: 'Upwork',
              url: 'https://www.upwork.com/freelancers/oluwatobiobadiah'
            },
            {
              name: 'Fiverr',
              url: 'https://www.fiverr.com/tobiobadiah'
            }
          ],
          from: '04/2018',
          to: '05/2020',
          assignments: [
            'Worked with clients across six continents in industries like transport and entertainment. Consistently delivered exceptional service, achieving 100% client satisfaction and securing repeat business, reflected in 30+ five-star reviews.'
          ]
        }
      ],
      education: [
        {
          degree: 'Masters of Science in Artificial Intelligence',
          school: 'De Montfort University',
          from: '10/2025',
          to: '09/2026'
        },
        {
          degree: 'Bachelor of Science in Computer Science',
          school: 'University of Buea',
          from: '10/2015',
          to: '03/2019'
        }
      ],
      certificates: [
        'Deep Reinforcement Learning for Enterprise — Udacity (2019)',
        'Deep Learning Nanodegree — Udacity (2019)'
      ]
    }
  },
  computed: {
    featuredResearch () {
      return HOME_FEATURED_SLUGS.map((slug) =>
        featuredProjects.find((p) => p.slug === slug)
      ).filter(Boolean)
    },
    moreMsc () {
      return featuredProjects.filter(
        (p) => !HOME_FEATURED_SLUGS.includes(p.slug)
      )
    }
  }
}
</script>

<style scoped>
.site {
  overflow-x: hidden;
}

.hero {
  position: relative;
  min-height: min(100vh, 820px);
  display: flex;
  align-items: stretch;
  background:
    linear-gradient(145deg, #d5dde8 0%, var(--paper) 42%, #eef2f6 100%);
  border-bottom: 1px solid var(--line);
}

.hero__grid {
  position: absolute;
  inset: 0;
  background-image:
    linear-gradient(rgba(18, 21, 28, 0.04) 1px, transparent 1px),
    linear-gradient(90deg, rgba(18, 21, 28, 0.04) 1px, transparent 1px);
  background-size: 48px 48px;
  mask-image: radial-gradient(ellipse 70% 60% at 30% 40%, #000 20%, transparent 75%);
  pointer-events: none;
}

.hero__glow {
  display: none;
}

.hero__inner {
  position: relative;
  z-index: 1;
  width: min(1120px, calc(100% - 3rem));
  margin: 0 auto;
  display: grid;
  grid-template-columns: 1.15fr 0.85fr;
  gap: 3rem;
  align-items: center;
  padding: 4.5rem 0 3.5rem;
}

.hero__eyebrow {
  margin: 0 0 0.75rem;
  font-family: var(--font-mono);
  font-size: 0.8rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--accent);
  font-weight: 500;
}

.hero__name {
  margin: 0;
  font-family: var(--font-display);
  font-size: clamp(3rem, 8vw, 5.25rem);
  font-weight: 700;
  line-height: 0.95;
  letter-spacing: -0.03em;
  color: var(--ink);
}

.hero__lede {
  margin: 1.25rem 0 0;
  max-width: 34ch;
  font-size: 1.125rem;
  color: var(--ink-muted);
  line-height: 1.55;
}

.hero__email {
  margin: 1rem 0 0;
  font-family: var(--font-mono);
  font-size: 0.85rem;
  color: var(--ink-muted);
}

.hero__socials {
  display: flex;
  gap: 0.65rem;
  margin-top: 1.5rem;
}

.social {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2.4rem;
  height: 2.4rem;
  color: var(--ink);
  background: transparent;
  border: 1px solid var(--line);
  border-radius: 999px;
  text-decoration: none;
  transition:
    color 0.25s var(--ease),
    background-color 0.25s var(--ease),
    border-color 0.25s var(--ease),
    transform 0.25s var(--ease);
}

.social svg {
  width: 1.05rem;
  height: 1.05rem;
}

.social:hover {
  color: #fff;
  background: var(--accent);
  border-color: var(--accent);
  transform: translateY(-2px);
}

.hero__nav {
  display: flex;
  flex-wrap: wrap;
  gap: 0.35rem 1.35rem;
  margin-top: 2rem;
  padding-top: 1.5rem;
  border-top: 1px solid var(--line);
}

.hero__nav a {
  position: relative;
  font-family: var(--font-display);
  font-size: 1rem;
  font-weight: 600;
  color: var(--ink);
  text-decoration: none;
  letter-spacing: -0.01em;
}

.hero__nav a::after {
  content: '';
  position: absolute;
  left: 0;
  bottom: -3px;
  width: 100%;
  height: 2px;
  background: var(--accent);
  transform: scaleX(0);
  transform-origin: left;
  transition: transform 0.3s var(--ease);
}

.hero__nav a:hover {
  color: var(--accent);
}

.hero__nav a:hover::after {
  transform: scaleX(1);
}

.hero__media {
  position: relative;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  height: min(68vh, 560px);
  min-height: 320px;
  align-self: stretch;
  /* Hard crop — removes the torso cut edge, no blur/fade */
  overflow: hidden;
  clip-path: inset(0 0 8% 0);
}

.hero__photo {
  width: 100%;
  height: 100%;
  object-fit: contain;
  object-position: center top;
  filter: saturate(0.95) contrast(1.03);
}

.section {
  padding: 5rem 0;
  background: var(--surface);
}

.section--tint {
  background:
    linear-gradient(180deg, #e4ebf3 0%, #edf1f6 100%);
}

.section__inner {
  width: min(var(--max), calc(100% - 3rem));
  margin: 0 auto;
}

.section__title {
  margin: 0 0 2rem;
  font-family: var(--font-display);
  font-size: clamp(1.75rem, 4vw, 2.25rem);
  font-weight: 700;
  letter-spacing: -0.02em;
  line-height: 1.1;
}

.section__title::after {
  content: '';
  display: block;
  width: 2.5rem;
  height: 3px;
  margin-top: 0.75rem;
  background: var(--accent);
}

.section__lede {
  margin: -0.75rem 0 1.75rem;
  max-width: 36rem;
  color: var(--ink-muted);
  line-height: 1.55;
}

.misc-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 0.65rem;
}

.misc-grid__item {
  margin: 0;
  overflow: hidden;
  border-radius: 4px;
  background: rgba(255, 255, 255, 0.35);
  border: 1px solid var(--line);
}

.misc-grid__item--tall {
  grid-row: span 2;
}

.misc-grid__media {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
  aspect-ratio: 4 / 5;
}

.misc-grid__item--tall .misc-grid__media {
  aspect-ratio: 3 / 4;
  min-height: 100%;
}

.misc-grid video.misc-grid__media {
  background: #0f1218;
}

.misc-grid__item--tall video.misc-grid__media {
  aspect-ratio: 9 / 16;
}

@media (max-width: 560px) {
  .misc-grid {
    grid-template-columns: 1fr 1fr;
    gap: 0.5rem;
  }

  .misc-grid__item--tall {
    grid-row: span 1;
  }

  .misc-grid__item--tall .misc-grid__media {
    aspect-ratio: 3 / 4;
  }

  .misc-grid__item--tall video.misc-grid__media {
    aspect-ratio: 9 / 16;
  }
}

.more-msc {
  margin: 1.75rem 0 0;
  padding-top: 1.25rem;
  border-top: 1px solid var(--line);
  font-size: 0.95rem;
  color: var(--ink-muted);
  line-height: 1.55;
}

.more-msc__label {
  display: block;
  margin-bottom: 0.35rem;
  font-family: var(--font-mono);
  font-size: 0.75rem;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--ink-muted);
}

.more-msc__link {
  color: var(--accent);
  text-decoration: none;
  border-bottom: 1px solid currentColor;
}

.more-msc__link:hover {
  color: var(--ink);
}

.more-msc__plain {
  color: var(--ink-muted);
}

.more-msc__sep {
  color: var(--ink-muted);
}

.prose p {
  margin: 0 0 1.15rem;
  color: var(--ink-muted);
}

.prose p:last-child {
  margin-bottom: 0;
}

.prose a {
  color: var(--accent);
  text-decoration: none;
  border-bottom: 1.5px solid currentColor;
}

.prose a:hover {
  color: var(--ink);
  border-bottom-color: var(--ink);
}

.timeline,
.experience-timeline,
.project-list {
  list-style: none;
  margin: 0;
  padding: 0;
}

.experience-timeline {
  position: relative;
  max-width: 46rem;
}

.experience-timeline::before {
  content: '';
  position: absolute;
  top: 0.55rem;
  bottom: 0.55rem;
  left: calc(5.75rem + 0.75rem);
  width: 1px;
  background: var(--line);
}

.experience-timeline__item + .experience-timeline__item {
  margin-top: 1.85rem;
}

.timeline__item + .timeline__item,
.project-list__item + .project-list__item {
  margin-top: 1.25rem;
  padding-top: 1.25rem;
  border-top: 1px solid var(--line);
}

.footer {
  background: var(--footer);
  color: rgba(255, 255, 255, 0.72);
  padding: 1.75rem 0;
}

.footer__inner {
  width: min(var(--max), calc(100% - 3rem));
  margin: 0 auto;
  font-family: var(--font-mono);
  font-size: 0.8rem;
  letter-spacing: 0.02em;
}

.reveal {
  opacity: 0;
  transform: translateY(18px);
  animation: rise 0.85s var(--ease) forwards;
  animation-delay: var(--d, 0s);
}

@keyframes rise {
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@media (max-width: 860px) {
  .hero {
    min-height: auto;
  }

  .hero__inner {
    grid-template-columns: 1fr;
    gap: 2rem;
    padding: 2.5rem 0 0;
    width: 100%;
  }

  .hero__copy {
    width: min(var(--max), calc(100% - 3rem));
    margin: 0 auto;
    order: 2;
    padding-bottom: 2.75rem;
  }

  .hero__media {
    order: 1;
    height: min(62vw, 400px);
    min-height: 260px;
    max-height: 420px;
    margin: 0 auto;
    width: min(420px, 88%);
    clip-path: inset(0 0 6% 0);
  }

  .hero__photo {
    width: 100%;
    margin-left: 0;
  }

  .hero__name {
    font-size: clamp(2.6rem, 12vw, 4rem);
  }

  .hero__lede {
    max-width: none;
  }
}

@media (max-width: 640px) {
  .experience-timeline::before {
    left: calc(4.75rem + 0.625rem);
  }

  .experience-timeline__item + .experience-timeline__item {
    margin-top: 1.5rem;
  }
}

@media (prefers-reduced-motion: reduce) {
  .reveal {
    opacity: 1;
    transform: none;
    animation: none;
  }

  .social,
  .hero__nav a::after {
    transition: none;
  }
}
</style>
